import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import * as Util from '../../../common/utilities';

import {
  Button,
  Card,
  Spinner,
  Tabs,
  TabsPanel,
  Toast,
  ToastContainer
} from '@salesforce/design-system-react';
import { withFlags } from '../../../flags/Flags';
import { withSession } from '../../../i18n/containers/AppInit.js';
import { FormattedHeading } from '../../../i18n/components/';
import ProfileUpload from '../../components/profiles/ProfileUpload';

import { EinsteinCard } from '../../../common/components/';
import { SetupContainer } from '../../components/';

import './SetupMenu.scss';

class SetupMenu extends Component {
  componentDidUpdate(prevProps) {
    if (
      Util.propChanged(prevProps, this.props, 'activation.isLoading', false)
    ) {
      if (this.props.activation.error) {
        this.props.actions.openToast(
          'getActivationFail',
          this.props.activation.error
        );
      }
    }

    if (
      Util.propChanged(prevProps, this.props, 'activation.isActivating', false)
    ) {
      if (this.props.activation.error) {
        this.props.actions.openToast(
          'activationFail',
          this.props.activation.error
        );
      } else {
        this.props.actions.openToast('activationSuccess');
      }
    }

    if (
      Util.propChanged(prevProps, this.props, 'activation.isToggling', false)
    ) {
      if (this.props.activation.error) {
        this.props.actions.openToast(
          'readonlyFail',
          this.props.activation.error
        );
      } else {
        this.props.actions.openToast('readonlySuccess');
        this.props.actions.getFlags();
      }
    }
  }

  getI18nID(suffix) {
    return `components.ecs.setup.${suffix}`;
  }

  // TODO: Tech debt: refactor to use ApiErrorToast instead of manually here (at least the errors)
  toast() {
    const { formatMessage } = this.props.intl;
    let toast;
    let variant;
    let duration;

    // Pick the right toast and variant to show
    if (this.props.toast.getActivationFail) {
      toast = 'getActivationFail';
      variant = 'error';
    } else if (this.props.toast.activationFail) {
      toast = 'activationFail';
      variant = 'error';
    } else if (this.props.toast.activationSuccess) {
      toast = 'activationSuccess';
      variant = 'success';
      duration = 5000;
    } else if (this.props.toast.readonlyFail) {
      toast = 'readonlyFail';
      variant = 'error';
    } else if (this.props.toast.readonlySuccess) {
      toast = 'readonlySuccess';
      variant = 'success';
      duration = 5000;
    }

    // Don't double notify for session errors
    if (toast && variant === 'error') {
      const { apiError } = this.props.toast[toast];
      if (apiError && apiError.sessionError) {
        toast = undefined;
      }
    }

    // render toast
    if (toast) {
      const heading = formatMessage({
        id: this.getI18nID(`toast.${toast}.heading`)
      });
      const details = formatMessage({
        id: this.getI18nID(`toast.${toast}.details`)
      });
      return (
        <Toast
          labels={{ heading, details }}
          variant={variant}
          duration={duration}
          onRequestClose={() => this.props.actions.closeToast(toast)}
        />
      );
    }
    return null;
  }

  canActivate() {
    const { isLoading, status } = this.props.activation;
    return !isLoading && status && status.toUpperCase() === 'INACTIVE';
  }

  isReadonly(flags) {
    if (flags && !flags.isReadonly) {
      return 'neutral';
    } else {
      return 'brand';
    }
  }

  requestActivation(session) {
    return () => {
      this.props.actions.activateEcs({ cToken: session.cToken });
    };
  }

  requestToggleReadonly(session) {
    return () => {
      this.props.actions.toggleReadonly({ cToken: session.cToken });
    };
  }

  renderReadOnly(flags, session) {
    const { formatMessage } = this.props.intl;
    const { isLoading, status } = this.props.activation;
    const isActive = !isLoading && status && status.toUpperCase() === 'ACTIVE';
    if (flags && flags.canReadonly && isActive) {
      return (
        <Button
          className="toggle-readonly"
          key="readonly"
          variant={this.isReadonly(flags)}
          onClick={this.requestToggleReadonly(session)}
          label={formatMessage({
            id: this.getI18nID('activate.readonly')
          })}
        />
      );
    }
    return null;
  }

  renderActivatePanel(session) {
    const { formatMessage } = this.props.intl;
    const { status } = this.props.activation;
    const cardProps = {
      title: formatMessage({ id: this.getI18nID('title') }),
      info: formatMessage({ id: this.getI18nID('info') })
    };
    const disabled = !this.canActivate();
    return withFlags(flags => (
      <div className="sub-content">
        <EinsteinCard {...cardProps} />
        <div className="slds-form activate-panel">
          <div className="step-info slds-col">
            <FormattedHeading id="components.ecs.setup.activate.heading" />
            <p>
              {formatMessage({
                id: this.getI18nID('activate.description')
              })}
            </p>
          </div>
          <div className="right-nav slds-col">
            <Button
              className="activate-button"
              key="activate"
              variant="neutral"
              disabled={disabled}
              onClick={this.requestActivation(session)}
              label={formatMessage({
                id: this.getI18nID(`activate.${status}`)
              })}
            />
            {this.renderReadOnly(flags, session)}
          </div>
        </div>
      </div>
    ));
  }

  renderSetup(session) {
    const { formatMessage } = this.props.intl;
    const { data } = this.props.rules;
    if (!this.canActivate() && !this.props.activation.error) {
      return withFlags(flags => (
        <Card className="setup-card" hasNoHeader={true}>
          <div className="slds-grid slds-wrap">
            <div className="slds-form setup-menu-container">
              <Tabs id="tabs-setup">
                <TabsPanel
                  label={formatMessage({
                    id: this.getI18nID('settings')
                  })}
                >
                  <SetupContainer
                    loadedRules={data.loaded}
                    actions={this.props.actions}
                    rules={data.rules}
                    flags={flags}
                    modals={this.props.modals}
                    assets={this.props.assets}
                    attributes={this.props.attributes}
                    session={session}
                  />
                </TabsPanel>
                <TabsPanel
                  label={formatMessage({
                    id: this.getI18nID('profile')
                  })}
                >
                  <ProfileUpload
                    actions={this.props.actions}
                    profileUpload={this.props.profileUpload}
                    toast={this.props.toast}
                    session={session}
                  />
                </TabsPanel>
              </Tabs>
            </div>
          </div>
        </Card>
      ));
    }
    return null;
  }

  render() {
    const spinner = (
      <div className="pi-common-spinner">
        <Spinner variant="brand" />
      </div>
    );
    if (this.props.activation.isLoading) {
      return spinner;
    }
    return withSession(session => {
      return (
        <div>
          {this.renderActivatePanel(session)}
          {this.renderSetup(session)}
          <ToastContainer>{this.toast()}</ToastContainer>
        </div>
      );
    });
  }
}

SetupMenu.propTypes = {
  actions: PropTypes.shape({
    getStatus: PropTypes.func,
    getRules: PropTypes.func,
    saveRules: PropTypes.func,
    getAssetTypes: PropTypes.func,
    getAttributes: PropTypes.func,
    searchAssets: PropTypes.func
  }),
  modals: PropTypes.object,
  assets: PropTypes.object,
  attributes: PropTypes.object,
  activation: PropTypes.object,
  rules: PropTypes.object,
  toast: PropTypes.object,
  location: PropTypes.object,
  profileUpload: PropTypes.object
};

export default injectIntl(SetupMenu);
